import React, {
  useState,
  useRef,
  useMemo,
  memo,
  useEffect,
  useLayoutEffect,
  useContext,
  useCallback
} from 'react';
import classNames from 'classnames';
import { Document } from 'react-pdf';
import VirtualList, { ListRef } from 'rc-virtual-list';
import {
  OverlayScrollbarsComponent,
  OverlayScrollbarsComponentRef
} from 'overlayscrollbars-react';
import PanelAction from '@pages/MediaViewer/AssetViewer/PanelAction';
import PlaybackPreparingLoader from '@pages/MediaViewer/AssetViewer/PlaybackPreparingLoader';
import { DocumentMetadataDto } from '@api/Api';
import { FabricContext } from '@context/FabricContext';
import FabricCanvas from '@pages/MediaViewer/AssetViewer/Canvas/Canvas';
import PageThumbnail from '@pages/MediaViewer/AssetViewer/PdfCard/components/PageThumbnail';
import { updateDrawingToolCursorHint } from '@pages/MediaViewer/AssetViewer/Canvas/DrawingToolCursorHint/DrawingToolCursorHint';
import { getDrawingToolCursor } from '@pages/MediaViewer/AssetViewer/Canvas/utils/cursors';
import PdfPage from '@pages/MediaViewer/AssetViewer/PdfCard/components/PdfPage';
import 'react-pdf/dist/Page/TextLayer.css';
import type { AssetViewerZoomOption } from '@context/AssetViewerStateContext/types';
import {
  PDF_PAGE_BASE_SIZE,
  getInitialPdfPageScale,
  getPdfPageSizes,
  handleSelectionChange
} from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { useAssetViewerState } from '@hooks/useAssetViewerState';
import type { PdfCardProps } from '@pages/MediaViewer/AssetViewer/PdfCard/PdfCard';
import { usePdfFabricCanvas } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/usePdfFabricCanvas';
import { usePdfScroll } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/usePdfScroll';
import { usePdfZoomScrollRestore } from '@pages/MediaViewer/AssetViewer/PdfCard/hooks/usePdfZoomScrollRestore';
import {
  ArrowLeftWithoutLineIcon,
  ArrowRightWithoutLineIcon,
  MediaViewerPagesIcon,
  SlideTextIcon
} from '@icons';
import {
  isAnnotationDrawTool,
  isCommentFormOpen,
  CURSOR_DRAW_TOOL
} from '@pages/MediaViewer/helpers/drawToolShape';

const minZoom = 0.1;
const maxZoom = 3;
const zoomStep = 0.1;
const pageVerticalPadding = 16;

const pdfPagesOpenKey = 'media-viewer-pdf-pages-open';

/** Whether the page thumbnails bar was left open (open by default) */
function getStoredPdfPagesOpen() {
  try {
    return localStorage.getItem(pdfPagesOpenKey) !== 'false';
  } catch {
    return true;
  }
}

function storePdfPagesOpen(isOpen: boolean) {
  try {
    localStorage.setItem(pdfPagesOpenKey, String(isOpen));
  } catch {
    // ignore
  }
}

function clampZoom(value: number) {
  return Math.min(maxZoom, Math.max(minZoom, value));
}

function extractTextFromNode(node: Node): string {
  if (node.nodeName === 'BR') return '\n';
  if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
  if (node.nodeType === Node.ELEMENT_NODE)
    return Array.from(node.childNodes).map(extractTextFromNode).join('');
  return '';
}

export default memo(function PdfCardDesktop({
  isExpand,
  setIsExpand,
  onReady,
  asset,
  isAssetReady,
  mobileActionVisibility
}: PdfCardProps) {
  const fabricContext = useContext(FabricContext);
  const {
    selectedCommentId,
    shapesDrawingTools,
    canvasMode,
    activePageNumber,
    zoomFactor,
    zoomOption,
    turnoverDegree,
    documentHighlight,
    selectComment,
    setActivePageNumber,
    setDocumentHighlight,
    setTurnoverDegree,
    setZoomFactor,
    setZoomOption,
    setCanvasMode,
    setActivePageHasText
  } = useAssetViewerState();
  const isShapeDrawing = shapesDrawingTools.shape !== CURSOR_DRAW_TOOL;
  const isTextHighlightMode = shapesDrawingTools.shape === 'documentHighlight';
  const isMainCanvasVisible =
    !!selectedCommentId ||
    (isAnnotationDrawTool(shapesDrawingTools.shape) && canvasMode === 'edit');
  // Viewing a comment (not editing it): the main canvas only shows it and
  // lets other pins be clicked. The cursor is the tool in use meanwhile (see
  // AssetViewerStateContext), so nothing is drawn there.
  const isViewingComment =
    !!selectedCommentId &&
    !isCommentFormOpen(selectedCommentId) &&
    canvasMode === 'view';
  const documentHighlightRef = useRef(documentHighlight);

  const listRef = useRef<ListRef>(null);
  const wrapperRef = useRef<OverlayScrollbarsComponentRef<'div'>>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const buttonSelectedTextRef = useRef<HTMLButtonElement>(null);
  const pageCanvasRef = useRef<HTMLCanvasElement>(null);
  const pageRefsMap = useRef(new Map<number, HTMLDivElement>());
  const skipActivePageScrollSyncRef = useRef(false);
  const isLoadPdf = useRef<boolean>(false);
  const pagesReadyRef = useRef(new Set<number>());
  const pageHasTextRef = useRef(new Map<number, boolean>());

  const textLayersMapRef = useRef<Map<HTMLDivElement, HTMLDivElement>>(
    new Map()
  );

  const fileUrl = useMemo(() => {
    const { proxy } = asset.metadata as DocumentMetadataDto;
    return proxy.url || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  useEffect(() => {
    isLoadPdf.current = false;
    pagesReadyRef.current.clear();
    pageHasTextRef.current.clear();
    setActivePageHasText(true);
    setIsPageSizeReady(false);
  }, [asset.id, setActivePageHasText]);

  const [pagesCount, setPagesCount] = useState(1);
  const [pageRefsVersion, setPageRefsVersion] = useState(0);
  const [pagesReadyVersion, setPagesReadyVersion] = useState(0);
  const [isShowPages, setIsShowPages] = useState<boolean>(
    getStoredPdfPagesOpen
  );
  const [isPageSizeReady, setIsPageSizeReady] = useState(false);
  // First page's base size: its size at zoom 100%. Annotation coordinates of
  // every page are relative to its width.
  const [pageSize, setPageSize] = useState<{ width: number; height: number }>({
    width: 1605,
    height: 2272
  });
  // Every page's size at scale 1 (rotation applied), read from the document
  const [pageNaturalSizes, setPageNaturalSizes] = useState<
    { width: number; height: number }[]
  >([]);
  // Viewport size, which the fit zoom options fit each page into
  const [viewportSize, setViewportSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  // Pages are rendered once their sizes and the viewport are known, so they
  // do not show at a default size first (and overflow horizontally)
  const isLayoutReady = isPageSizeReady && !!viewportSize;

  // The pages' scroll container, once the scrollbars are set up: page
  // annotation canvases cover the part in view
  const [scrollViewport, setScrollViewport] = useState<HTMLElement | null>(
    null
  );
  useEffect(() => {
    if (!isLayoutReady) return undefined;
    const frameId = window.requestAnimationFrame(() => {
      setScrollViewport(
        wrapperRef.current?.osInstance()?.elements()?.viewport ?? null
      );
    });
    return () => window.cancelAnimationFrame(frameId);
  }, [isLayoutReady]);

  const pagesArray = useMemo(
    () =>
      new Array(pagesCount).fill('').map((_, index) => ({ page: index + 1 })),
    [pagesCount]
  );

  // Pages share the first page's scale, so they keep their true relative
  // sizes (a page twice as large shows twice as large at the same zoom)
  const pageBaseSizes = useMemo(() => {
    const firstNaturalSize = pageNaturalSizes[0];
    const baseScale = firstNaturalSize
      ? pageSize.width / firstNaturalSize.width
      : 1;
    return pagesArray.map(({ page }) => {
      const naturalSize = pageNaturalSizes[page - 1];
      return naturalSize
        ? {
            width: naturalSize.width * baseScale,
            height: naturalSize.height * baseScale
          }
        : pageSize;
    });
  }, [pagesArray, pageNaturalSizes, pageSize]);

  // Zoom fitting a page of this base size for an option
  const getFitZoom = useCallback(
    (
      option: NonNullable<AssetViewerZoomOption>,
      size: { width: number; height: number }
    ) => {
      if (option === 'actual-size' || !viewportSize) return clampZoom(1);
      const radians = turnoverDegree * (Math.PI / 180);
      const width =
        Math.abs(size.width * Math.cos(radians)) +
        Math.abs(size.height * Math.sin(radians));
      const height =
        Math.abs(size.width * Math.sin(radians)) +
        Math.abs(size.height * Math.cos(radians));
      const containerHeight = Math.max(
        0,
        viewportSize.height - pageVerticalPadding
      );
      const scaleWidth = Math.floor(viewportSize.width / (width / 100)) / 100;
      const scaleHeight = Math.floor(containerHeight / (height / 100)) / 100;
      if (option === 'fit-width') {
        return clampZoom(scaleWidth);
      }
      if (option === 'fit-height') {
        return clampZoom(scaleHeight);
      }
      // Auto fits the whole page, like fit page
      if (option === 'auto' || option === 'fit-page') {
        return clampZoom(Math.min(scaleWidth, scaleHeight));
      }
      return clampZoom(1);
    },
    [viewportSize, turnoverDegree]
  );

  // Fit options fit every page on its own, other zooms apply to all pages
  const isFitZoomOption = !!zoomOption && zoomOption !== 'actual-size';
  const pageZooms = useMemo(
    () =>
      pageBaseSizes.map((size) =>
        isFitZoomOption && zoomOption
          ? getFitZoom(zoomOption, size)
          : zoomFactor
      ),
    [pageBaseSizes, isFitZoomOption, zoomOption, getFitZoom, zoomFactor]
  );
  const pageRenderSizes = useMemo(
    () =>
      pageBaseSizes.map((size, index) => ({
        width: size.width * pageZooms[index],
        height: size.height * pageZooms[index]
      })),
    [pageBaseSizes, pageZooms]
  );
  // Annotations zoom, relative to the first page's base width
  const pageAnnotationZooms = useMemo(
    () => pageRenderSizes.map((size) => size.width / pageSize.width),
    [pageRenderSizes, pageSize.width]
  );
  const pageHeights = useMemo(
    () => pageRenderSizes.map((size) => size.height),
    [pageRenderSizes]
  );
  const activePageIndex = Math.max(
    0,
    Math.min(activePageNumber, pagesCount) - 1
  );
  const activePageZoom = pageZooms[activePageIndex] ?? zoomFactor;
  const activeAnnotationZoom =
    pageAnnotationZooms[activePageIndex] ?? zoomFactor;

  const onRegisterPageRoot = useCallback(
    (page: number, element: HTMLDivElement | null) => {
      const hadElement = pageRefsMap.current.has(page);
      if (element) {
        pageRefsMap.current.set(page, element);
        if (!hadElement) setPageRefsVersion((value) => value + 1);
        return;
      }
      pageRefsMap.current.delete(page);
      if (hadElement) setPageRefsVersion((value) => value + 1);
    },
    []
  );

  const onPageLoadSuccess = useCallback((page: number) => {
    if (pagesReadyRef.current.has(page)) return;
    pagesReadyRef.current.add(page);
    setPagesReadyVersion((value) => value + 1);
  }, []);

  const onPageTextContent = useCallback(
    (page: number, hasText: boolean) => {
      pageHasTextRef.current.set(page, hasText);
      if (page === activePageNumber) setActivePageHasText(hasText);
    },
    [activePageNumber, setActivePageHasText]
  );

  useEffect(() => {
    setActivePageHasText(
      pageHasTextRef.current.get(activePageNumber) !== false
    );
  }, [activePageNumber, setActivePageHasText]);

  const onSelectText = (
    e: React.MouseEvent<HTMLButtonElement> | React.TouchEvent<HTMLButtonElement>
  ) => {
    e.preventDefault();
    setDocumentHighlight(documentHighlightRef.current);
  };

  const { runCanvasLayout, onClearHighlightObjects, renderTextHighlights } =
    usePdfFabricCanvas({
      fabricContext,
      wrapperRef,
      contentRef,
      canvasContainerRef,
      pageRefsMap,
      activePageNumber,
      zoomFactor: activeAnnotationZoom,
      turnoverDegree,
      isPageSizeReady,
      selectedCommentId,
      pageRefsVersion,
      pagesReadyVersion,
      isMainCanvasVisible,
      isMainCanvasPassThrough: isViewingComment,
      selectComment,
      setCanvasMode,
      setDocumentHighlight
    });

  const {
    renderRange,
    visiblePageNumber,
    updateActivePageFromScroll,
    setActivePageInView
  } = usePdfScroll({
    wrapperRef,
    pageRefsMap,
    skipActivePageScrollSyncRef,
    pagesArray,
    pagesCount,
    pageHeights,
    // With a drawing tool any page in view can be drawn on: the page under
    // the cursor is the active one
    isActivePageFollowingPointer: isShapeDrawing,
    isPageSizeReady,
    pageRefsVersion,
    activePageNumber,
    selectedCommentId,
    shapesDrawingTools,
    runCanvasLayout,
    setActivePageNumber,
    selectComment,
    setDocumentHighlight,
    setCanvasMode
  });

  // Whether something is on the drawing canvas: shapes, a pin, or the boxes
  // of a text selection (being highlighted)
  const [hasCanvasDrawing, setHasCanvasDrawing] = useState(false);
  useEffect(() => {
    const { canvas } = fabricContext;
    if (!canvas) return undefined;
    const update = () => setHasCanvasDrawing(canvas.getObjects().length > 0);
    update();
    canvas.on('object:added', update);
    canvas.on('object:removed', update);
    canvas.on('canvas:cleared', update);
    return () => {
      canvas.off('object:added', update);
      canvas.off('object:removed', update);
      canvas.off('canvas:cleared', update);
    };
  }, [fabricContext]);

  // Any page in view can be drawn on: the page under the cursor becomes the
  // active one. Once drawing started, it stays on that page until the comment
  // is sent or cancelled. Only a comment's drawing: a viewed one's stays on
  // the canvas once closed (e.g. with Esc), with the drawing tool back.
  const isDrawingComment =
    isCommentFormOpen(selectedCommentId) || canvasMode === 'edit';
  const isDrawingLocked =
    isShapeDrawing &&
    ((isDrawingComment && hasCanvasDrawing) ||
      !!documentHighlight.rects?.length);

  // The tool's cursor over every page, not only over the drawing canvas on
  // the active one: a page scrolled under a resting cursor shows it before
  // it becomes the active one
  const pagesStyle = useMemo(
    () =>
      isShapeDrawing
        ? ({
            '--pdf-page-cursor': getDrawingToolCursor({
              shape: shapesDrawingTools.shape,
              color: shapesDrawingTools.color,
              canvasMode
            })
          } as React.CSSProperties)
        : undefined,
    [
      isShapeDrawing,
      shapesDrawingTools.shape,
      shapesDrawingTools.color,
      canvasMode
    ]
  );

  // Last pointer event over the pages. Kept outside the effect below, which
  // runs again on every active page change: scrolling under a resting cursor
  // keeps making the page under it active, not only the first time.
  const lastPointerEventRef = useRef<PointerEvent | null>(null);
  useEffect(() => {
    const content = contentRef.current;
    if (!content || !isLayoutReady) return undefined;
    const onPointerEvent = (e: PointerEvent) => {
      lastPointerEventRef.current = e;
    };
    const onPointerLeave = () => {
      lastPointerEventRef.current = null;
    };
    // Down and up too, for the buttons held (e.g. selecting text)
    content.addEventListener('pointermove', onPointerEvent);
    content.addEventListener('pointerdown', onPointerEvent);
    content.addEventListener('pointerup', onPointerEvent);
    content.addEventListener('pointerleave', onPointerLeave);
    return () => {
      content.removeEventListener('pointermove', onPointerEvent);
      content.removeEventListener('pointerdown', onPointerEvent);
      content.removeEventListener('pointerup', onPointerEvent);
      content.removeEventListener('pointerleave', onPointerLeave);
      lastPointerEventRef.current = null;
    };
  }, [isLayoutReady]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content || !isShapeDrawing || !isLayoutReady) return undefined;
    const hintText = 'You can annotate one page at a time';
    let isHintShown = false;
    const hideHint = () => {
      if (!isHintShown) return;
      isHintShown = false;
      updateDrawingToolCursorHint(fabricContext.cursorHintRef.current, {
        hidden: true
      });
    };
    const onPointerAt = (target: Element | null, e: PointerEvent) => {
      const pageEl = target?.closest<HTMLElement>(
        '.media_viewer_pdf-mobile-page'
      );
      const page = Number(pageEl?.dataset.pageNumber) || null;
      if (!page || page === activePageNumber) {
        hideHint();
        return;
      }
      if (!isDrawingLocked) {
        // Not while a button is held, e.g. selecting text across pages
        if (!e.buttons) setActivePageInView(page);
        return;
      }
      isHintShown = true;
      updateDrawingToolCursorHint(fabricContext.cursorHintRef.current, {
        text: hintText,
        event: e
      });
    };
    const onPointerMove = (e: PointerEvent) => {
      onPointerAt(e.target as Element | null, e);
    };
    const onPointerLeave = () => {
      hideHint();
    };
    // Scrolling moves pages under a resting cursor
    const onScroll = () => {
      const lastPointerEvent = lastPointerEventRef.current;
      if (!lastPointerEvent) return;
      const { clientX, clientY } = lastPointerEvent;
      onPointerAt(
        document.elementFromPoint(clientX, clientY),
        lastPointerEvent
      );
    };
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    content.addEventListener('pointermove', onPointerMove);
    content.addEventListener('pointerleave', onPointerLeave);
    viewport?.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      content.removeEventListener('pointermove', onPointerMove);
      content.removeEventListener('pointerleave', onPointerLeave);
      viewport?.removeEventListener('scroll', onScroll);
      hideHint();
    };
  }, [
    isShapeDrawing,
    isLayoutReady,
    isDrawingLocked,
    activePageNumber,
    setActivePageInView,
    fabricContext.cursorHintRef
  ]);

  // While drawing, the page being drawn on cannot be scrolled out of view:
  // at least a quarter of the viewport keeps showing it
  useEffect(() => {
    if (!isDrawingLocked) return undefined;
    const viewport = wrapperRef.current?.osInstance()?.elements()?.viewport;
    if (!viewport) return undefined;

    // Allowed scrollTop range
    const getScrollBounds = () => {
      const pageEl = pageRefsMap.current.get(activePageNumber);
      if (!pageEl) return null;
      const viewportRect = viewport.getBoundingClientRect();
      const pageRect = pageEl.getBoundingClientRect();
      const minVisible = Math.min(pageRect.height, viewportRect.height * 0.25);
      const { scrollTop } = viewport;
      return {
        min: scrollTop + pageRect.top - (viewportRect.bottom - minVisible),
        max: scrollTop + pageRect.bottom - (viewportRect.top + minVisible)
      };
    };
    const clamp = (value: number, min: number, max: number) =>
      Math.min(max, Math.max(min, value));

    // Wheel and touchpad (including inertia): stop exactly at the limit,
    // instead of correcting after scrolling past it, which shakes
    const onWheel = (e: WheelEvent) => {
      const bounds = getScrollBounds();
      if (!bounds || !e.deltaY) return;
      const lineHeight = 40;
      const deltaY =
        e.deltaMode === 1
          ? e.deltaY * lineHeight
          : e.deltaMode === 2
          ? e.deltaY * viewport.clientHeight
          : e.deltaY;
      const next = viewport.scrollTop + deltaY;
      if (next >= bounds.min && next <= bounds.max) return;
      e.preventDefault();
      viewport.scrollTop = clamp(next, bounds.min, bounds.max);
    };
    // Scrollbar dragging, keyboard, etc.
    const onScroll = () => {
      const bounds = getScrollBounds();
      if (!bounds) return;
      const { scrollTop } = viewport;
      const clamped = clamp(scrollTop, bounds.min, bounds.max);
      if (clamped !== scrollTop) viewport.scrollTop = clamped;
    };

    viewport.addEventListener('wheel', onWheel, { passive: false });
    viewport.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      viewport.removeEventListener('wheel', onWheel);
      viewport.removeEventListener('scroll', onScroll);
    };
  }, [isDrawingLocked, activePageNumber]);

  const { captureZoomScrollAnchor } = usePdfZoomScrollRestore({
    wrapperRef,
    pageRefsMap,
    skipActivePageScrollSyncRef,
    activePageNumber,
    zoomFactor,
    pageRefsVersion,
    runCanvasLayout,
    updateActivePageFromScroll
  });

  const prevHighlightRectsLengthRef = useRef(
    documentHighlight.rects?.length ?? 0
  );

  useEffect(() => {
    const prevLength = prevHighlightRectsLengthRef.current;
    const currentLength = documentHighlight.rects?.length ?? 0;
    prevHighlightRectsLengthRef.current = currentLength;

    if (currentLength > 0) return;

    documentHighlightRef.current = { text: '', rects: [] };
    onClearHighlightObjects();

    if (prevLength > 0) {
      document.getSelection()?.removeAllRanges();
    }
  }, [documentHighlight.rects?.length, onClearHighlightObjects]);

  useEffect(() => {
    if (!isShapeDrawing) return;
    if (shapesDrawingTools.shape !== 'documentHighlight') return;
    const buttonSelectedText = buttonSelectedTextRef.current;
    const pageCanvas = pageCanvasRef.current;
    let prevRange: Range | null = null;

    const onSelectionchange = () => {
      textLayersMapRef.current.clear();
      pageRefsMap.current.forEach((pageRoot) => {
        const textLayerDiv = pageRoot?.querySelector('.textLayer');
        const endDiv = pageRoot?.querySelector('.endOfContent');
        if (!textLayerDiv || !endDiv) return;
        textLayersMapRef.current.set(
          textLayerDiv as HTMLDivElement,
          endDiv as HTMLDivElement
        );
      });

      const range = handleSelectionChange(textLayersMapRef.current, prevRange);
      prevRange = range;

      if (buttonSelectedText) {
        buttonSelectedText.style.display = 'none';
        buttonSelectedText.style.pointerEvents = 'none';
      }

      if (!range) {
        onClearHighlightObjects();
        return;
      }

      const pageRoot = pageRefsMap.current.get(activePageNumber);
      const pageCoordsElement =
        pageRoot?.querySelector('.media_viewer_pdf-inner') ?? pageCanvas;

      if (!pageCoordsElement) {
        onClearHighlightObjects();
        return;
      }

      const rects = Array.from(range.getClientRects()).filter(
        (r) => r.width > 0 && r.height > 0
      );
      if (!rects.length) {
        onClearHighlightObjects();
        if (buttonSelectedText) {
          buttonSelectedText.style.display = 'none';
        }
        return;
      }
      const canvasRect = pageCoordsElement.getBoundingClientRect();
      const selectedCoords = rects
        .map((rect) => ({
          left: ((rect.left - canvasRect.left) / canvasRect.width) * 100,
          top: ((rect.top - canvasRect.top) / canvasRect.height) * 100,
          width: (rect.width / canvasRect.width) * 100,
          height: (rect.height / canvasRect.height) * 100
        }))
        .filter((r) => r.width > 0 && r.height > 0);

      if (!selectedCoords.length) {
        onClearHighlightObjects();
        return;
      }

      const fragment = range.cloneContents();
      documentHighlightRef.current = {
        text: Array.from(fragment.childNodes)
          .map(extractTextFromNode)
          .join('')
          .trim(),
        rects: selectedCoords
      };
      renderTextHighlights(selectedCoords);

      const textLayerDiv = pageRoot?.querySelector('.textLayer');
      const contentEl = contentRef.current;
      if (!textLayerDiv || !contentEl) return;
      const contentRect = contentEl.getBoundingClientRect();
      const lastRect = rects[rects.length - 1];
      const buttonWidth = buttonSelectedText?.offsetWidth || 0;
      const buttonHeight = buttonSelectedText?.offsetHeight || 0;
      const left = Math.min(
        Math.max(lastRect.right - contentRect.left + 8, 0),
        contentRect.width - buttonWidth
      );
      const top = Math.min(
        Math.max(lastRect.bottom - contentRect.top + 8, 0),
        contentRect.height - buttonHeight
      );
      if (buttonSelectedText) {
        buttonSelectedText.style.display = 'block';
        buttonSelectedText.style.left = `${left}px`;
        buttonSelectedText.style.top = `${top}px`;
      }
    };

    const onMouseUp = () => {
      if (!buttonSelectedText) return;
      buttonSelectedText.style.pointerEvents = 'auto';
    };

    const onTouchEnd = () => {
      if (!buttonSelectedText) return;
      buttonSelectedText.style.pointerEvents = 'auto';
    };

    const hideSelectedTextButton = () => {
      if (!buttonSelectedText) return;
      buttonSelectedText.style.display = 'none';
      buttonSelectedText.style.pointerEvents = 'none';
    };

    // Esc first clears the text selection (with its highlight and button),
    // keeping the tool to select again; the next Esc cancels the comment
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const hasSelection =
        !!document.getSelection()?.toString().trim() ||
        !!documentHighlightRef.current?.rects?.length;
      if (!hasSelection) return;
      // Before (capture) and instead of cancelling the comment
      e.preventDefault();
      prevRange = null;
      document.getSelection()?.removeAllRanges();
      documentHighlightRef.current = { text: '', rects: [] };
      onClearHighlightObjects();
      hideSelectedTextButton();
      setDocumentHighlight(null);
    };

    document.addEventListener('selectionchange', onSelectionchange);
    document.addEventListener('mouseup', onMouseUp);
    document.addEventListener('touchend', onTouchEnd);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      prevRange = null;
      textLayersMapRef.current.clear();
      document.removeEventListener('selectionchange', onSelectionchange);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('keydown', onKeyDown, true);
      // Not left behind when the tool changes before the selection clears
      hideSelectedTextButton();
    };
  }, [
    selectedCommentId,
    shapesDrawingTools,
    isShapeDrawing,
    activePageNumber,
    setDocumentHighlight,
    onClearHighlightObjects,
    renderTextHighlights
  ]);

  useEffect(() => {
    if (shapesDrawingTools.shape !== 'documentHighlight') return;
    if (!documentHighlightRef.current?.rects?.length) return;
    renderTextHighlights(documentHighlightRef.current.rects);
  }, [zoomFactor, shapesDrawingTools.shape, renderTextHighlights]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTo({
      index: activePageNumber - 1
    });
  }, [isShowPages, activePageNumber]);

  // Zoom of the active page for an option (fit options size pages individually)
  const getZoomFactorFromOption = useCallback(
    (option: NonNullable<AssetViewerZoomOption>) => {
      if (!isPageSizeReady) return 1;
      return getFitZoom(option, pageBaseSizes[activePageIndex] ?? pageSize);
    },
    [isPageSizeReady, getFitZoom, pageBaseSizes, activePageIndex, pageSize]
  );

  const setZoom = useCallback(
    (v: number | NonNullable<AssetViewerZoomOption>) => {
      if (typeof v === 'number') {
        const newZoom = clampZoom(v);
        captureZoomScrollAnchor(newZoom);
        setZoomFactor(newZoom);
      } else {
        const newZoomFactor = getZoomFactorFromOption(v);
        captureZoomScrollAnchor(newZoomFactor);
        setZoomFactor(newZoomFactor, v);
      }
    },
    [getZoomFactorFromOption, setZoomFactor, captureZoomScrollAnchor]
  );

  // Layout effect, like applying the zoom below: settled before first paint
  useLayoutEffect(() => {
    setZoomOption('auto');
  }, [asset.id, setZoomOption]);

  // Layout effect: the viewport is measured (so pages are sized) before the
  // pages are first painted
  useLayoutEffect(() => {
    if (!isPageSizeReady) return undefined;

    const container =
      wrapperRef.current?.osInstance()?.elements()?.viewport ??
      wrapperRef.current?.getElement();
    if (!container) return undefined;

    const measure = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      setViewportSize((prev) =>
        prev && prev.width === width && prev.height === height
          ? prev
          : { width, height }
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, [isPageSizeReady]);

  // With a fit option the zoom shown (and the start for zooming in or out)
  // is the active page's
  useLayoutEffect(() => {
    if (!isFitZoomOption || !zoomOption || !viewportSize) return;
    if (activePageZoom === zoomFactor) return;
    setZoomFactor(activePageZoom, zoomOption);
  }, [
    isFitZoomOption,
    zoomOption,
    viewportSize,
    activePageZoom,
    zoomFactor,
    setZoomFactor
  ]);

  const onChangePage = useCallback(
    (page: number) => {
      setActivePageNumber(page);
      selectComment(null);
      setDocumentHighlight(null);
      setCanvasMode('view');
    },
    [setActivePageNumber, selectComment, setDocumentHighlight, setCanvasMode]
  );

  const onLoadSuccess = useCallback(
    (pdfPage: { originalWidth: number; originalHeight: number }) => {
      if (isLoadPdf.current) return;
      isLoadPdf.current = true;
      const { originalWidth, originalHeight } = pdfPage;
      setPageSize(
        getInitialPdfPageScale(
          originalWidth,
          originalHeight,
          PDF_PAGE_BASE_SIZE,
          maxZoom,
          false
        )
      );
      setIsPageSizeReady(true);
    },
    []
  );

  const showPanelAction =
    !mobileActionVisibility || mobileActionVisibility.showPanelAction;

  const panelActionConfig = useMemo(
    () => ({
      zoom: zoomFactor,
      zoomOption,
      setZoom,
      isShapeDrawingToolsOpen: isShapeDrawing,
      isExpand,
      setIsExpand: (v: boolean) => {
        setIsExpand(v);
      },
      setRotate: (v: number) => {
        setTurnoverDegree(turnoverDegree + v === 360 ? 0 : turnoverDegree + v);
      }
    }),
    [
      zoomFactor,
      zoomOption,
      setZoom,
      isShapeDrawing,
      isExpand,
      setIsExpand,
      turnoverDegree,
      setTurnoverDegree
    ]
  );

  return (
    <Document
      className={classNames('media_viewer_pdf', {
        'b-show-pages': isShowPages,
        'b-hide-shapes-drawing':
          shapesDrawingTools?.shape === 'documentHighlight' && isShapeDrawing,
        'b-shapes-drawing': isShapeDrawing,
        'b-pages-drawable': true
      })}
      file={fileUrl}
      onLoadSuccess={(pdf) => {
        setPagesCount(pdf.numPages);
        onReady();
        // Size the pages from the document, before any page is rendered
        getPdfPageSizes(pdf).then((sizes) => {
          if (!sizes.length) return;
          setPageNaturalSizes(sizes);
          onLoadSuccess({
            originalWidth: sizes[0].width,
            originalHeight: sizes[0].height
          });
        });
      }}
      loading={<PlaybackPreparingLoader delayed />}
    >
      {isShowPages && (
        <div className="media_viewer_pdf-sidebar">
          {isPageSizeReady && (
            <VirtualList
              className="media_viewer_pdf-list"
              data={pagesArray}
              height={wrapperRef?.current?.getElement()?.offsetHeight || 500}
              fullHeight={true}
              itemKey="page"
              ref={listRef}
            >
              {(item) => {
                return (
                  <PageThumbnail
                    page={item.page}
                    active={item.page === activePageNumber}
                    maxWidth={102}
                    maxHeight={147}
                    defaultAspectRatio={
                      pageNaturalSizes[item.page - 1]
                        ? pageNaturalSizes[item.page - 1].height /
                          pageNaturalSizes[item.page - 1].width
                        : pageSize.height / pageSize.width
                    }
                    onClick={onChangePage}
                  />
                );
              }}
            </VirtualList>
          )}
        </div>
      )}
      <div className="media_viewer_pdf-actions">
        <div className="media_viewer_pdf-actions-indicator">
          <button
            className="media_viewer_pdf-btn"
            type="button"
            disabled={activePageNumber === 1}
            onClick={() => onChangePage(activePageNumber - 1)}
          >
            <ArrowLeftWithoutLineIcon />
          </button>
          {visiblePageNumber} of {pagesCount}
          <button
            disabled={activePageNumber === pagesCount}
            className="media_viewer_pdf-btn"
            type="button"
            onClick={() => onChangePage(activePageNumber + 1)}
          >
            <ArrowRightWithoutLineIcon />
          </button>
        </div>
        <button
          className="media_viewer_pdf-btn media_viewer_pdf-action-btn tour-text-highlight-pdf-action"
          type="button"
          onClick={() => {
            setIsShowPages(!isShowPages);
            storePdfPagesOpen(!isShowPages);
          }}
        >
          <MediaViewerPagesIcon />
        </button>
      </div>
      {showPanelAction && (
        <PanelAction
          {...panelActionConfig}
          minZoom={minZoom}
          zoomStep={zoomStep}
          showFitPage
        />
      )}
      <OverlayScrollbarsComponent
        className="media_viewer_pdf-wrapper"
        ref={wrapperRef}
      >
        <div
          className={classNames(
            'media_viewer_pdf-content',
            'media_viewer_pdf-content-mobile'
          )}
          ref={contentRef}
        >
          <div
            className="media_viewer_pdf-mobile-pages media_viewer_pdf-mobile-pages-desktop"
            style={pagesStyle}
            onContextMenu={
              isTextHighlightMode ? (e) => e.preventDefault() : undefined
            }
          >
            {isLayoutReady &&
              pagesArray.map(({ page }) => (
                <PdfPage
                  key={page}
                  pageNumber={page}
                  pageWidth={pageRenderSizes[page - 1].width}
                  pageHeight={pageRenderSizes[page - 1].height}
                  zoomFactor={pageAnnotationZooms[page - 1]}
                  turnoverDegree={turnoverDegree}
                  isActive={page === activePageNumber}
                  isDrawingDisabled={
                    isDrawingLocked && page !== activePageNumber
                  }
                  isMainCanvasVisible={isMainCanvasVisible}
                  isViewingComment={isViewingComment}
                  isFirstPage={page === 1}
                  shouldRender={
                    page >= renderRange.start && page <= renderRange.end
                  }
                  scrollViewport={scrollViewport}
                  pageCanvasRef={pageCanvasRef}
                  onRegisterPageRoot={onRegisterPageRoot}
                  onFirstPageLoadSuccess={onLoadSuccess}
                  onPageLoadSuccess={onPageLoadSuccess}
                  onPageTextContent={onPageTextContent}
                />
              ))}
          </div>
          <button
            ref={buttonSelectedTextRef}
            className="media_viewer_pdf-selected-btn"
            type="button"
            onClick={onSelectText}
            style={{ display: 'none' }}
          >
            <SlideTextIcon />
          </button>
          <div ref={canvasContainerRef} className="media_viewer_pdf-canvas">
            <FabricCanvas />
          </div>
        </div>
      </OverlayScrollbarsComponent>
      {(!isAssetReady || !isLayoutReady) && <PlaybackPreparingLoader delayed />}
    </Document>
  );
});
